/// <amd-module name="Artifacts/Maven/components/MavenPackageOverview" />
import { MavenInstallTabInstructions } from "Artifacts/Maven/components/MavenInstallTabInstructions";
import * as MavenContracts from "Artifacts/Maven/Contracts/Maven";
import { getFiles, IMavenFile, isSnapshotVersion } from "Artifacts/Maven/helpers/MavenFileHelper";
import {
  getGradlePackageInstallText,
  getMavenPackageInstallText,
  renderGradlePackageInstallText,
  renderMavenPackageInstallText,
} from "Artifacts/Maven/helpers/MavenInstallTextHelper";
import * as MavenRes from "Artifacts/Maven/Resources";
import { IMavenRestApiUrlGeneratorService } from "Artifacts/Maven/services/MavenRestApiUrlGeneratorService";
import { PackageDeletedMessageCard } from "Artifacts/Package/components/package-details-common/PackageDeletedMessageCard";
import { PackageTile } from "Artifacts/Package/components/package-details-common/PackageTile";
import * as PackageRes from "Artifacts/Package/Resources";
import { IProtocolOverviewProps } from "Artifacts/Root/protocols/types";
import * as RootRes from "Artifacts/Root/Resources";
import { connectToFeed } from "Artifacts/Root/routing/Constants";
import { IFeed } from "Artifacts/Root/types/IFeed";
import { IPackage } from "Artifacts/Root/types/IPackage";
import { IPackageVersion } from "Artifacts/Root/types/IPackageVersion";
import * as React from "react";
import { generatePath, useHistory } from "react-router-dom";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ISimpleListCell, SimpleList } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

const MavenOverview: React.FC<IProtocolOverviewProps> = ({ feed, pkg, version }) => {
  const history = useHistory();
  const { pageContext } = useComponentContext();

  const minimumSnapshotInstanceCount = feed.accountMetadata.minimumSnapshotInstanceCount;
  const isSnapShotVersion: boolean = isSnapshotVersion(version);
  const protocolMetadata = version.protocolMetadata;
  const mavenMetadata = (protocolMetadata && protocolMetadata.data
    ? protocolMetadata.data
    : null) as MavenContracts.MavenPomMetadata;

  return (
    <div className="package-overview-content">
      {version.isDeleted && (
        <PackageDeletedMessageCard version={version.version} packageName={pkg.name} />
      )}
      {!version.isDeleted && (
        <PackageTile title={PackageRes.PackageDetails_GetThisPackage}>
          <div className="package-command">
            <Button
              iconProps={{
                iconName: "PlugConnected",
              }}
              onClick={() => {
                history.push(generatePath(connectToFeed, { feedIdentifier: feed?.name }));
              }}
            >
              {RootRes.ConnectToFeedButton}
            </Button>

            <span className="install-instructions-then">
              {PackageRes.PackageDetails_InstallInstructions_then}
            </span>
            <MavenInstallTabInstructions
              mavenCommand={getMavenPackageInstallText(pkg.name, version.version)}
              gradleCommand={getGradlePackageInstallText(pkg.name, version.version)}
            />
          </div>
        </PackageTile>
      )}
      {mavenMetadata && mavenMetadata.parent && (
        <PackageTile title={MavenRes.Maven_Metadata_Parent}>
          <div className="package-command parent-artifact-tile">
            <MavenInstallTabInstructions
              mavenCommand={renderMavenPackageInstallText(
                mavenMetadata.parent.groupId,
                mavenMetadata.parent.artifactId,
                mavenMetadata.parent.version,
              )}
              gradleCommand={renderGradlePackageInstallText(
                mavenMetadata.parent.groupId,
                mavenMetadata.parent.artifactId,
                mavenMetadata.parent.version,
              )}
            />
          </div>
        </PackageTile>
      )}

      {version.description && version.description.length > 0 && (
        <PackageTile title={PackageRes.PackageDetails_Description}>
          <div className="package-description">{version.description}</div>
        </PackageTile>
      )}

      {!version.isDeleted && (
        <PackageTile title={MavenRes.Maven_Files_Title}>
          <SimpleList
            itemProvider={
              new ArrayItemProvider<ISimpleListCell>(
                getFiles(version).map((file) => renderFile(pageContext, feed, pkg, version, file)),
              )
            }
          />
          <div>
            {isSnapShotVersion
              ? StringUtils.format(
                  MavenRes.Maven_SnapshotRetentionPolicy_Label,
                  minimumSnapshotInstanceCount,
                )
              : ""}
          </div>
        </PackageTile>
      )}
    </div>
  );
};

function renderFile(
  pageContext: IVssPageContext,
  feed: IFeed,
  pkg: IPackage,
  version: IPackageVersion,
  file: IMavenFile,
): ISimpleListCell {
  let feedId = feed.id;

  if (feed.selectedViewId) {
    feedId = feed.id + "@" + feed.selectedViewId;
  }

  return {
    href: pageContext
      .getService<IMavenRestApiUrlGeneratorService>("IMavenRestApiUrlGeneratorService")
      .getFileDownloadUrl(
        feedId,
        pkg.name,
        version.normalizedVersion,
        file.key,
        feed && feed.project != null,
        feed.accountMetadata.publicAccessMapping,
      ),
    textClassName: "package-link-text",
    hrefRel: "noopener noreferrer",
    text: file.name,
  } as ISimpleListCell;
}

export { MavenOverview };
